import { useState } from 'react';
import { ChevronRight, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { proposalSlides } from '@/constants/proposalSlides';
import { trackEvent } from '@/utils/analytics';

/**
 * SampleProposalModal
 * 提案書サンプルをモーダル内に表示する。
 * Figma Make v3 資料修正版の 10 枚スライド画像を縦に並べる構成。
 */
interface SampleProposalModalProps {
  onClose: () => void;
  onOpenContactModal: () => Promise<void>;
}

export function SampleProposalModal({
  onClose,
  onOpenContactModal,
}: SampleProposalModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    trackEvent('cta_click', { location: 'sample_modal' });
    setIsSubmitting(true);
    try {
      await onOpenContactModal();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4"
      onClick={onClose}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/60" />

      {/* Modal Content */}
      <div
        className="relative bg-white rounded-xl sm:rounded-2xl max-w-5xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="border-b border-[#E5E7EB] px-4 sm:px-8 py-4 sm:py-6 flex items-center justify-between">
          <h2
            className="text-[#111827] text-lg sm:text-xl lg:text-2xl"
            style={{ fontWeight: 700 }}
          >
            提案資料サンプル
          </h2>
          <button
            onClick={onClose}
            className="text-[#6B7280] hover:text-[#111827] transition-colors p-1"
            aria-label="閉じる"
          >
            <X className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
        </div>

        {/* Modal Body - Scrollable Slide Images */}
        <div className="flex-1 overflow-y-auto bg-[#F9FAFB] p-3 sm:p-6 lg:p-8">
          <div className="flex flex-col items-center gap-3 sm:gap-6 lg:gap-8">
            {proposalSlides.map((slideSrc, idx) => (
              <img
                key={idx}
                src={slideSrc}
                alt={`提案資料ページ ${idx + 1}`}
                className="w-full max-w-4xl rounded-lg"
                style={{
                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
                }}
              />
            ))}
          </div>
        </div>

        {/* Modal Footer */}
        <div className="border-t border-[#E5E7EB] bg-white px-4 sm:px-8 py-4 sm:py-6">
          <p
            className="text-[#111827] text-center mb-3 sm:mb-4 text-xs sm:text-sm"
            style={{ fontWeight: 600 }}
          >
            実際の提案書は、ご入力いただいたWebサイトに合わせてカスタマイズされます
          </p>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="w-full bg-[#0264BF] hover:bg-[#01508A] text-white rounded-lg transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base font-bold h-10 sm:h-12"
          >
            {isSubmitting ? '確認中...' : '無料で提案資料を受け取る'}
            {!isSubmitting && <ChevronRight className="ml-1 sm:ml-2 w-4 h-4 sm:w-5 sm:h-5" />}
          </Button>
        </div>
      </div>
    </div>
  );
}
